import type { AttendanceMark, ClassFill } from "./enums.ts";
import { DEFAULT_TIMEZONE, localTime } from "./time.ts";

/**
 * Check-in by name at the start of a class (I1, Pilates studio shape). Where clients pick each class
 * (`class_fill = pick_class`), the class list opens 15 minutes before the class. The instructor taps
 * each client who comes, and each tap saves at once (a `checkin` mark: present, with
 * `bookings.checked_in_at`). A tap after the start is a late arrival. At 10 minutes after the start,
 * the worker job `check-in-absent` marks the rest absent, if the owner turned that on (off by default). A family
 * business (`fixed_group`) keeps the cards and the list as before.
 *
 * The marks themselves keep the rules of every mark: they open on the session's day in the business
 * clock and stay open after it (`attendanceOpen`). The 15 minutes only open the check-in taps.
 *
 * The phone and Desk read their words from `checkInModel`, so both say the same thing.
 */

/** The class list opens this many minutes before the class. */
export const CHECK_IN_OPENS_MIN = 15;
/** The job marks the rest absent this many minutes after the start. */
export const AUTO_ABSENT_AFTER_MIN = 10;

/** Check-in is the way to take attendance where clients pick each class. */
export function usesCheckIn(classFill: ClassFill): boolean {
  return classFill === "pick_class";
}

/**
 * The start value of "mark the rest absent" (`settings.autoAbsent`): off. The instructor marks who did
 * not come until the owner turns it on (D45, decided by Anas).
 */
export const AUTO_ABSENT_DEFAULT = false;

export function checkInOpensAt(startsAt: Date): Date {
  return new Date(startsAt.getTime() - CHECK_IN_OPENS_MIN * 60_000);
}

export function autoAbsentAt(startsAt: Date): Date {
  return new Date(startsAt.getTime() + AUTO_ABSENT_AFTER_MIN * 60_000);
}

/** The check-in taps work from 15 minutes before the class. */
export function checkInOpen(startsAt: Date, now: Date): boolean {
  return now.getTime() >= checkInOpensAt(startsAt).getTime();
}

/** A check-in after the start is a late arrival. */
export function isLateCheckIn(checkedInAt: Date, startsAt: Date): boolean {
  return checkedInAt.getTime() > startsAt.getTime();
}

export type CheckInEntry = {
  bookingId: string;
  attendance?: AttendanceMark | null;
  /** When the person was checked in (ISO). Null for a mark made with the cards or the list. */
  checkedInAt?: string | null;
  /** Absent, marked by the job (no person made the mark). */
  autoAbsent?: boolean;
};

export type CheckInState = "waiting" | "here" | "late" | "absent";

export type CheckInRow = {
  bookingId: string;
  state: CheckInState;
  /** "Here 17:52", "Late 18:04", "Absent", "Not here yet". */
  label: string;
  /** A second line, or null: "Teamna marked this at 18:10". */
  detail: string | null;
  /** What a tap does: check the person in, or take the check-in back. */
  tap: "check_in" | "undo";
};

export type CheckInPhase = "before" | "open" | "started" | "closed";

export type CheckInModel = {
  phase: CheckInPhase;
  /** False until 15 minutes before the class. */
  canTap: boolean;
  title: string;
  text: string;
  counts: { here: number; late: number; absent: number; waiting: number };
  /** "4 here (1 late), 2 absent, 1 not here yet". */
  summary: string;
  rows: CheckInRow[];
};

/**
 * The check-in screen of one class, now. `autoAbsent` is the time the job marks the rest absent,
 * or null when the business turned it off.
 */
export function checkInModel(input: {
  startsAt: Date;
  now: Date;
  autoAbsent: Date | null;
  roster: readonly CheckInEntry[];
  participantLabel: { one: string; many: string };
  timeZone?: string;
}): CheckInModel {
  const tz = input.timeZone ?? DEFAULT_TIMEZONE;
  const at = (instant: Date) => localTime(instant, tz);
  const now = input.now.getTime();
  const start = input.startsAt.getTime();
  const opens = checkInOpensAt(input.startsAt);
  const closed = input.autoAbsent !== null && now >= input.autoAbsent.getTime();
  const phase: CheckInPhase = now < opens.getTime() ? "before" : now < start ? "open" : closed ? "closed" : "started";
  const one = input.participantLabel.one;

  const counts = { here: 0, late: 0, absent: 0, waiting: 0 };
  const rows: CheckInRow[] = input.roster.map((entry) => {
    if (entry.attendance === "present") {
      counts.here += 1;
      const checkedIn = entry.checkedInAt ? new Date(entry.checkedInAt) : null;
      const late = checkedIn !== null && isLateCheckIn(checkedIn, input.startsAt);
      if (late) counts.late += 1;
      return {
        bookingId: entry.bookingId,
        state: late ? "late" : "here",
        label: checkedIn ? `${late ? "Late" : "Here"} ${at(checkedIn)}` : "Here",
        detail: null,
        tap: "undo",
      };
    }
    if (entry.attendance === "absent") {
      counts.absent += 1;
      return {
        bookingId: entry.bookingId,
        state: "absent",
        label: "Absent",
        detail: entry.autoAbsent && input.autoAbsent ? `Teamna marked this at ${at(input.autoAbsent)}` : null,
        tap: "check_in",
      };
    }
    counts.waiting += 1;
    return { bookingId: entry.bookingId, state: "waiting", label: phase === "closed" ? "No mark" : "Not here yet", detail: null, tap: "check_in" };
  });

  const parts = [`${counts.here} here${counts.late > 0 ? ` (${counts.late} late)` : ""}`];
  if (counts.absent > 0) parts.push(`${counts.absent} absent`);
  if (counts.waiting > 0) parts.push(`${counts.waiting} not here yet`);

  const startText = at(input.startsAt);
  let title: string;
  let text: string;
  switch (phase) {
    case "before":
      title = `Check-in opens at ${at(opens)}`;
      text = `The class list opens ${CHECK_IN_OPENS_MIN} minutes before the class. Then tap each ${one} who comes.`;
      break;
    case "open":
      title = "Check-in is open";
      text = `Tap each ${one} who comes. The class starts at ${startText}.`;
      break;
    case "started":
      title = `The class started at ${startText}`;
      text = input.autoAbsent
        ? `Tap a ${one} who comes late. At ${at(input.autoAbsent)}, Teamna marks the rest absent. You can change a mark after that.`
        : `Tap a ${one} who comes late. Use the list to mark who did not come.`;
      break;
    case "closed":
      title = "Check-in is closed";
      // The job marks the rest only when the list was used: one check-in or more (`jobs/check-in-absent.ts`).
      // The words come from the marks themselves: only the job's marks say Teamna made them, and only
      // a class with a check-in by name waits for the job (internal review R4.1).
      {
        const autoMarked = input.roster.filter((entry) => entry.attendance === "absent" && entry.autoAbsent).length;
        const checkedInByName = input.roster.some((entry) => entry.attendance === "present" && !!entry.checkedInAt);
        text =
          counts.waiting === 0
            ? autoMarked === 0
              ? "Everyone has a mark. Tap a name to change it."
              : `Teamna marked ${autoMarked} absent at ${at(input.autoAbsent!)}. Tap a name to change it.`
            : checkedInByName
              ? "Teamna marks the rest absent soon. Tap a name to change it."
              : `Nobody was checked in by name, so Teamna marks nobody absent. Tap each ${one} who came, or use the list.`;
      }
      break;
  }
  return { phase, canTap: phase !== "before", title, text, counts, summary: parts.join(", "), rows };
}

/** A tap on the check-in list that shows before the stored mark comes back: Desk and the phone. */
export type CheckInTap = { attendance: "present" | null; at: string };

/** The list as the instructor sees it: each tap shows at once, over the stored mark. */
export function withCheckInTaps<T extends CheckInEntry>(people: readonly T[], taps: Readonly<Record<string, CheckInTap>>): T[] {
  return people.map((person) => {
    const tap = taps[person.bookingId];
    if (!tap) return person;
    return { ...person, attendance: tap.attendance, checkedInAt: tap.attendance ? tap.at : null, autoAbsent: false };
  });
}

/** The taps the stored marks caught up with (and not saving now): their own picture can go. */
export function settledCheckInTaps(
  people: readonly CheckInEntry[],
  taps: Readonly<Record<string, CheckInTap>>,
  saving: Readonly<Record<string, unknown>>,
): string[] {
  return people
    .filter((person) => {
      const tap = taps[person.bookingId];
      return tap !== undefined && (person.attendance ?? null) === tap.attendance && !saving[person.bookingId];
    })
    .map((person) => person.bookingId);
}
